/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {Fragment} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    Alert,
    Button,
    Divider,
    Heading,
    Stack,
    Box,
    Text,
    useStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {AlertIcon} from '../icons'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import ResetPasswordFields from '../../components/forms/reset-password-fields'
import useTagManager from '../../hooks/use-tag-manager'

const ResetPasswordForm = ({submitForm, clickSignIn = noop, form}) => {
    const styles = useStyleConfig('LoginStyles')
    const formStyles = useStyleConfig('FormStyles')
    const tagManager = useTagManager()
    return (
        <Fragment>
            <Stack justify="center" align="center" spacing={8}>
                <Stack spacing="10px">
                    <Heading {...styles.modal.heading}>
                        <FormattedMessage
                            defaultMessage="Reset Password"
                            id="reset_password_form.title.reset_password"
                        />
                    </Heading>
                    <Divider {...styles.modal.divider} />
                    <Box {...styles.modal.descriptionBox}>
                        <Text {...styles.modal.descriptionText}>
                            <FormattedMessage
                                defaultMessage="Enter the email associated with your account. You will receive an email with a link to reset your password."
                                id="password.reset.description"
                            />
                        </Text>
                    </Box>
                </Stack>
            </Stack>
            <form onSubmit={form.handleSubmit(submitForm)} data-testid="sf-auth-modal-form">
                <Stack spacing="20px" {...formStyles.fieldsStack}>
                    {form.formState.errors?.global && (
                        <Alert status="error">
                            <AlertIcon color="red.500" boxSize={4} />
                            <Text fontSize="sm" ml={3}>
                                {form.formState.errors.global.message}
                            </Text>
                        </Alert>
                    )}
                    <ResetPasswordFields form={form} />
                    <Stack spacing={6}>
                        <Button
                            type="submit"
                            {...styles.modal.button}
                            onClick={() => {
                                form.clearErrors('global')
                                tagManager.ctaClick(
                                    'cta_click',
                                    'authentication',
                                    'reset_password_submit',
                                    'cart_reset_password',
                                    'Reset Password'
                                )
                            }}
                            isLoading={form.formState.isSubmitting}
                        >
                            <FormattedMessage
                                defaultMessage="Reset Password"
                                id="reset_password_form.button.reset_password"
                            />
                        </Button>

                        <Stack direction="row" spacing={1} justify="center">
                            <Text {...styles.modal.descriptionText}>
                                <FormattedMessage
                                    defaultMessage="Or return to"
                                    id="reset_password_form.message.return_to_sign_in"
                                    description="Precedes link to return to sign in"
                                />
                            </Text>
                            <Button variant="link" onClick={clickSignIn} {...styles.modal.link}>
                                <FormattedMessage
                                    defaultMessage="Sign in"
                                    id="reset_password_form.action.sign_in"
                                />
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            </form>
        </Fragment>
    )
}

ResetPasswordForm.propTypes = {
    submitForm: PropTypes.func,
    clickSignIn: PropTypes.func,
    form: PropTypes.object
}

export default ResetPasswordForm
